import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  TextField,
  Grid,
  Pagination,
  FormControlLabel,
  Switch,
  InputAdornment,
  MenuItem,
} from "@mui/material";
import Sidebar from "../Sidebar/Sidebar";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreFromTrashRoundedIcon from "@mui/icons-material/RestoreFromTrashRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import TagRoundedIcon from "@mui/icons-material/TagRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import MapRoundedIcon from "@mui/icons-material/MapRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import PhoneAndroidRoundedIcon from "@mui/icons-material/PhoneAndroidRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import ToggleOnRoundedIcon from "@mui/icons-material/ToggleOnRounded";
import { useNavigate } from "react-router-dom";
import config from "../../config";

const emptyForm = {
  mandiPartner: "",
  mandiCode: "",
  mandiLocation: "",
  state: "",
  contactNumber: "",
  alternateNumber: "",
  email: "",
  businessName: "",
  isActive: true,
  isIVRCaller: false,
};

const limit = 20;

const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

export default function MandiPartner() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [partners, setPartners] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [ivrFilter, setIvrFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [formErrors, setFormErrors] = useState({});

  const numericFields = ["contactNumber", "alternateNumber", "mandiCode"];
  const maxLengths = {
    contactNumber: 10,
    alternateNumber: 10,
    mandiCode: 6,
  };

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const stateOptions = useMemo(() => {
    return [...new Set(partners.map((p) => p.state).filter(Boolean))].sort(
      (a, b) => a.localeCompare(b)
    );
  }, [partners]);

  const filteredPartners = useMemo(() => {
    return partners.filter((p) => {
      if (statusFilter !== "all") {
        const wantActive = statusFilter === "true";
        if (Boolean(p.isActive) !== wantActive) return false;
      }

      if (ivrFilter !== "all") {
        const wantIvr = ivrFilter === "true";
        if (Boolean(p.isIVRCaller) !== wantIvr) return false;
      }

      if (stateFilter !== "all" && p.state !== stateFilter) return false;

      return true;
    });
  }, [partners, statusFilter, ivrFilter, stateFilter]);

  const requestIdRef = useRef(0);

  const fetchPartners = useCallback(async () => {
    const requestId = ++requestIdRef.current;

    try {
      setRefreshing(true);
      const token = sessionStorage.getItem("token");
      const params = new URLSearchParams({
        page,
        limit,
      });

      if (searchTerm) {
        params.set("search", searchTerm);
      }

      const res = await fetch(
        `${config.apiBaseUrl}/mandi-partner/get-all?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await res.json();

      if (requestId !== requestIdRef.current) return;

      if (data.success) {
        setPartners(data.data || []);
        setTotal(data.total || 0);
      } else {
        setPartners([]);
        setTotal(0);
      }
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      setPartners([]);
      setTotal(0);
    } finally {
      if (requestId === requestIdRef.current) setRefreshing(false);
    }
  }, [page, searchTerm]);

  const lastFetchKeyRef = useRef(null);

  useEffect(() => {
    const key = `${page}|${searchTerm}`;
    if (lastFetchKeyRef.current === key) {
      return;
    }
    lastFetchKeyRef.current = key;
    fetchPartners();
  }, [page, searchTerm, fetchPartners]);

  const applySearch = () => {
    const next = searchInput.trim();
    if (next === searchTerm && page === 1) {
      fetchPartners();
      return;
    }

    setSearchTerm(next);
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    if (searchTerm) {
      setSearchTerm("");
      setPage(1);
    }
  };

  const openAddForm = () => {
    setFormMode("add");
    setFormData(emptyForm);
    setFormErrors({});
    setEditingId(null);
    setFormOpen(true);
  };

  const openEditForm = (partner) => {
    setFormMode("edit");
    setFormData({
      mandiPartner: partner.mandiPartner || "",
      mandiCode: partner.mandiCode || "",
      mandiLocation: partner.mandiLocation || "",
      state: partner.state || "",
      contactNumber: partner.contactNumber || "",
      alternateNumber: partner.alternateNumber || "",
      email: partner.email || "",
      businessName: partner.businessName || "",
      isActive: partner.isActive !== false,
      isIVRCaller: partner.isIVRCaller === true,
    });
    setEditingId(partner._id);
    setFormErrors({});
    setFormOpen(true);
  };

  const closeForm = () => {
    if (!formLoading) {
      setFormOpen(false);
      setFormData(emptyForm);
      setFormErrors({});
      setEditingId(null);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;

    if (numericFields.includes(name)) {
      const digitsOnly = value.replace(/\D/g, "").slice(0, maxLengths[name]);
      setFormData((prev) => ({ ...prev, [name]: digitsOnly }));
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const errors = {};

    numericFields.forEach((field) => {
      const value = formData[field]?.trim();
      const max = maxLengths[field];

      if (field === "contactNumber" && !value) {
        errors[field] = "Contact number is required";
      } else if (field === "mandiCode" && !value) {
        errors[field] = "Mandi code is required";
      } else if (value && !/^\d+$/.test(value)) {
        errors[field] = "Only numbers are allowed";
      } else if (value && value.length > max) {
        errors[field] = `Maximum ${max} digits allowed`;
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setFormLoading(true);
      const token = sessionStorage.getItem("token");

      const isEdit = formMode === "edit";
      const url = isEdit
        ? `${config.apiBaseUrl}/mandi-partner/update/${editingId}`
        : `${config.apiBaseUrl}/mandi-partner/create`;

      const payload = {
        mandiPartner: formData.mandiPartner.trim(),
        mandiCode: formData.mandiCode.trim(),
        mandiLocation: formData.mandiLocation.trim(),
        state: formData.state.trim(),
        contactNumber: formData.contactNumber.trim(),
        alternateNumber: formData.alternateNumber.trim(),
        email: formData.email.trim(),
        businessName: formData.businessName.trim(),
        isActive: formData.isActive,
        isIVRCaller: formData.isIVRCaller,
      };

      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok && data.success !== false) {
        setSnackbar({
          open: true,
          message:
            data.message ||
            (isEdit
              ? "Mandi partner updated successfully"
              : "Mandi partner added successfully"),
          severity: "success",
        });

        setFormOpen(false);
        setFormData(emptyForm);
        setEditingId(null);

        if (!isEdit && page !== 1) {
          setPage(1);
        } else {
          fetchPartners();
        }
      } else {
        setSnackbar({
          open: true,
          message: data.message || "Something went wrong",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    } finally {
      setFormLoading(false);
    }
  };

  const openDeleteDialog = (partner) => {
    setSelectedPartner(partner);
    setDeleteDialog(true);
  };

  const closeDeleteDialog = () => {
    if (!deleteLoading) {
      setDeleteDialog(false);
      setSelectedPartner(null);
    }
  };

  const handleDelete = async () => {
    if (!selectedPartner) return;

    try {
      setDeleteLoading(true);
      const token = sessionStorage.getItem("token");
      const res = await fetch(
        `${config.apiBaseUrl}/mandi-partner/delete/${selectedPartner._id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await res.json();

      setDeleteDialog(false);
      setSelectedPartner(null);

      if (res.ok) {
        setSnackbar({
          open: true,
          message: data.message || "Mandi partner deleted successfully",
          severity: "success",
        });

        if (partners.length === 1 && page > 1) {
          setPage((prev) => prev - 1);
        } else {
          await fetchPartners();
        }
      } else {
        setSnackbar({
          open: true,
          message: data.message || "Failed to delete mandi partner",
          severity: "error",
        });
      }
    } catch (error) {
      setDeleteDialog(false);
      setSelectedPartner(null);

      setSnackbar({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchInput("");
    setSearchTerm("");
    setPage(1);
    setStatusFilter("all");
    setIvrFilter("all");
    setStateFilter("all");
  };


  const goToPartnerDetail = (partner) => {
    if (!partner?._id) return;
    navigate(`/mandi-partner/${partner._id}`);
  };

  const goToDeletedPartners = () => {
    navigate("/mandi-partner/deleted");
  };

  const isCompact = isMobile;

  const headCellSx = {
    px: isCompact ? 0.85 : 1.5,
    py: isCompact ? 0.75 : 1,
    fontSize: isCompact ? "0.58rem" : "0.72rem",
    fontWeight: 700,
    letterSpacing: isCompact ? "0.02em" : "normal",
    textTransform: isCompact ? "uppercase" : "none",
    color: isCompact ? "rgba(15,23,42,0.55)" : "#0F172A",
    background: "#F8FAFC",
    borderBottom: "2px solid rgba(15,23,42,0.10)",
    borderRight: "1px solid rgba(15,23,42,0.10)",
    whiteSpace: "nowrap",
  };
  const bodyCellSx = {
    px: isCompact ? 0.85 : 1.5,
    py: isCompact ? 0.75 : 1,
    fontSize: isCompact ? "0.68rem" : "0.78rem",
    borderBottom: "1px solid rgba(15,23,42,0.08)",
    borderRight: "1px solid rgba(15,23,42,0.08)",
    whiteSpace: "nowrap",
  };
  const stickyColSx = isMobile
    ? {
        position: "sticky",
        left: 0,
        zIndex: 1,
        boxShadow: "2px 0 4px -2px rgba(15,23,42,0.18)",
      }
    : {};

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", background: "#F8FAFC" }}>
      <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, overflowX: "hidden" }}>
        <Box sx={{ p: { xs: 2, md: 4 }, minWidth: 0 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            rowGap={2}
            mb={4}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              {isMobile && (
                <IconButton
                  onClick={() => setMobileOpen(true)}
                  sx={{
                    border: "1px solid #E5E7EB",
                    borderRadius: "12px",
                  }}
                >
                  <MenuRoundedIcon />
                </IconButton>
              )}

              <Typography variant="h4" fontWeight={800}>
                Mandi Partners
              </Typography>

              {refreshing && (
                <CircularProgress size={20} sx={{ ml: 1 }} />
              )}
            </Stack>

            <Stack direction="row" spacing={1.5} flexWrap="wrap" rowGap={1}>
              <Button
                variant="outlined"
                startIcon={<RestoreFromTrashRoundedIcon />}
                onClick={goToDeletedPartners}
                sx={{
                  borderRadius: "12px",
                  px: 3,
                  py: 1.2,
                  textTransform: "none",
                  fontWeight: 600,
                  borderColor: "#E2E8F0",
                  color: "#334155",
                  "&:hover": { borderColor: "#CBD5E1", bgcolor: "#F8FAFC" },
                }}
              >
                View Deleted Partners
              </Button>

              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={openAddForm}
                sx={{
                  borderRadius: "12px",
                  px: 3,
                  py: 1.2,
                  textTransform: "none",
                  fontWeight: 600,
                  boxShadow: "none",
                  background: "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg,#1D4ED8 0%,#0891B2 100%)",
                  },
                }}
              >
                Add Mandi Partner
              </Button>
            </Stack>
          </Stack>

          <Paper sx={{ p: 2, mb: 3,borderRadius: 3,boxShadow: "0 4px 12px rgba(0,0,0,0.06)",}} >
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by partner name"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") applySearch();
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon sx={{ color: "#6B7280" }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchInput && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={handleClearSearch}>
                          <ClearRoundedIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "#F9FAFB",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={1.5}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "#F9FAFB",
                    },
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6} md={1.5}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="IVR Call"
                  value={ivrFilter}
                  onChange={(e) => {
                    setIvrFilter(e.target.value);
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "#F9FAFB",
                    },
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="State"
                  value={stateFilter}
                  onChange={(e) => {
                    setStateFilter(e.target.value);
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "#F9FAFB",
                    },
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  {stateOptions.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={applySearch}
                  sx={{
                    height: 40,
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 600,
                    boxShadow: "none",
                    background: "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)",
                    "&:hover": {
                      background: "linear-gradient(135deg,#1D4ED8 0%,#0891B2 100%)",
                    },
                  }}
                >
                  Apply
                </Button>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  onClick={clearFilters}
                  sx={{
                    height: 40,
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Clear
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <Box sx={{ position: "relative" }}>
            {refreshing && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(248,250,252,0.6)",
                  borderRadius: 3,
                }}
              >
                <CircularProgress size={32} />
              </Box>
            )}

            <Box
              sx={{
                opacity: refreshing ? 0.5 : 1,
                pointerEvents: refreshing ? "none" : "auto",
                transition: "opacity 0.15s ease",
              }}
            >
              <Paper sx={{ borderRadius: 3, overflow: "hidden", maxWidth: "100%" }}>
                <TableContainer sx={{ overflowX: "auto", maxWidth: "100%", WebkitOverflowScrolling: "touch" }}>
                  <Table sx={{ minWidth: isMobile ? 620 : 780 }} size={isMobile ? "small" : "medium"}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...headCellSx, ...stickyColSx, minWidth: isMobile ? 70 : 85 }}>
                          Mandi Code
                        </TableCell>
                        <TableCell sx={{ ...headCellSx, minWidth: isMobile ? 100 : 130 }}>Partner Name</TableCell>
                        <TableCell sx={{ ...headCellSx, minWidth: isMobile ? 90 : 115 }}>Mandi Location</TableCell>
                        <TableCell sx={{ ...headCellSx, minWidth: isMobile ? 65 : 85 }}>State</TableCell>
                        <TableCell sx={{ ...headCellSx, minWidth: isMobile ? 85 : 105 }}>Contact Number</TableCell>
                        <TableCell sx={{ ...headCellSx, minWidth: isMobile ? 62 : 78 }}>Status</TableCell>
                        <TableCell sx={{ ...headCellSx, minWidth: isMobile ? 62 : 78 }}>IVR Call</TableCell>
                        <TableCell sx={{ ...headCellSx, borderRight: "none", minWidth: isMobile ? 72 : 95 }} align="center">
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filteredPartners.length > 0 ? (
                        filteredPartners.map((partner, idx) => {
                          const zebraSx = idx % 2 === 1 ? { background: "rgba(15,23,42,0.015)" } : {};
                          const stickyBg = idx % 2 === 1 ? "#FBFCFD" : "#fff";
                          return (
                            <TableRow key={partner._id} sx={zebraSx}>
                              <TableCell sx={{ ...bodyCellSx, ...stickyColSx, background: stickyBg }}>
                                <Box
                                  component="button"
                                  type="button"
                                  onClick={() => goToPartnerDetail(partner)}
                                  sx={{
                                    background: "none",
                                    border: "none",
                                    padding: 0,
                                    margin: 0,
                                    cursor: "pointer",
                                    fontWeight: 700,
                                    fontSize: "inherit",
                                    fontFamily: "inherit",
                                    color: "#2563EB !important",
                                    textDecoration: "none",
                                    "&:hover": { textDecoration: "underline" },
                                  }}
                                >
                                  {partner.mandiCode || "—"}
                                </Box>
                              </TableCell>
                              <TableCell sx={bodyCellSx}>{partner.mandiPartner || "—"}</TableCell>
                              <TableCell sx={bodyCellSx}>{partner.mandiLocation || "—"}</TableCell>
                              <TableCell sx={bodyCellSx}>{partner.state || "—"}</TableCell>
                              <TableCell sx={bodyCellSx}>{partner.contactNumber || "—"}</TableCell>
                              <TableCell sx={bodyCellSx}>
                                <Chip
                                  label={partner.isActive ? "Active" : "Inactive"}
                                  size="small"
                                  color={partner.isActive ? "success" : "default"}
                                  sx={{ height: isCompact ? 20 : 22, fontSize: isCompact ? "0.6rem" : "0.68rem", "& .MuiChip-label": { px: 0.8 } }}
                                />
                              </TableCell>
                              <TableCell sx={bodyCellSx}>
                                <Chip
                                  label={partner.isIVRCaller ? "Yes" : "No"}
                                  size="small"
                                  color={partner.isIVRCaller ? "secondary" : "default"}
                                  variant={partner.isIVRCaller ? "filled" : "outlined"}
                                  sx={{ height: isCompact ? 20 : 22, fontSize: isCompact ? "0.6rem" : "0.68rem", "& .MuiChip-label": { px: 0.8 } }}
                                />
                              </TableCell>
                              <TableCell sx={{ ...bodyCellSx, borderRight: "none" }} align="center">
                                <Stack direction="row" spacing={0} justifyContent="center">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => openEditForm(partner)}
                                  >
                                    <EditRoundedIcon sx={{ fontSize: isMobile ? 16 : 18 }} />
                                  </IconButton>

                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => openDeleteDialog(partner)}
                                  >
                                    <DeleteIcon sx={{ fontSize: isMobile ? 16 : 18 }} />
                                  </IconButton>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 4, borderRight: "none", borderBottom: "none" }}>
                            No mandi partners found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
              {isMobile && filteredPartners.length > 0 && (
                <Typography sx={{ fontSize: "0.68rem", color: "rgba(15,23,42,0.45)", mt: 0.75, ml: 0.25 }}>
                  Swipe left for more →
                </Typography>
              )}

              {total > 0 && totalPages > 1 && (
                <Stack alignItems="center" mt={4}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                    shape="rounded"
                    siblingCount={isMobile ? 0 : 1}
                    boundaryCount={1}
                    size={isMobile ? "small" : "medium"}
                    showFirstButton={!isMobile}
                    showLastButton={!isMobile}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: "#6B7280", mt: 1 }}
                  >
                    Page {page} of {totalPages} · {total} total
                  </Typography>
                </Stack>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Dialog open={formOpen} onClose={closeForm} fullWidth maxWidth="md">
        <DialogTitle fontWeight={700}>
          {formMode === "edit" ? "Edit Mandi Partner" : "Add Mandi Partner"}
        </DialogTitle>

        <form onSubmit={handleFormSubmit}>
          <DialogContent sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Partner Name"
                  name="mandiPartner"
                  value={formData.mandiPartner}
                  onChange={handleFormChange}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonRoundedIcon sx={{ fontSize: 20, color: "#9CA3AF" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Mandi Code"
                  name="mandiCode"
                  value={formData.mandiCode}
                  onChange={handleFormChange}
                  required
                  error={Boolean(formErrors.mandiCode)}
                  helperText={formErrors.mandiCode || ""}
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    maxLength: 6,
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <TagRoundedIcon sx={{ fontSize: 20, color: "#9CA3AF" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Mandi Location"
                  name="mandiLocation"
                  value={formData.mandiLocation}
                  onChange={handleFormChange}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOnRoundedIcon sx={{ fontSize: 20, color: "#9CA3AF" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  select
                  fullWidth
                  label="State"
                  name="state"
                  value={formData.state}
                  onChange={handleFormChange}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MapRoundedIcon sx={{ fontSize: 20, color: "#9CA3AF" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                >
                  {INDIAN_STATES.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Business Name"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleFormChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <StorefrontRoundedIcon sx={{ fontSize: 20, color: "#9CA3AF" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Contact Number"
                  name="contactNumber"
                  value={formData.contactNumber}
                  onChange={handleFormChange}
                  required
                  error={Boolean(formErrors.contactNumber)}
                  helperText={formErrors.contactNumber || ""}
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    maxLength: 10,
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneRoundedIcon sx={{ fontSize: 20, color: "#9CA3AF" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Alternate Number"
                  name="alternateNumber"
                  value={formData.alternateNumber}
                  onChange={handleFormChange}
                  error={Boolean(formErrors.alternateNumber)}
                  helperText={formErrors.alternateNumber || ""}
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    maxLength: 10,
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneAndroidRoundedIcon sx={{ fontSize: 20, color: "#9CA3AF" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  type="email"
                  label="Email"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailRoundedIcon sx={{ fontSize: 20, color: "#9CA3AF" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                />
              </Grid>
            </Grid>

            <Typography
              variant="caption"
              sx={{ display: "block", color: "#9CA3AF", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", mt: 3, mb: 1 }}
            >
              Preferences
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    border: "1px solid #E5E7EB",
                    borderRadius: "12px",
                    px: 2,
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <ToggleOnRoundedIcon sx={{ fontSize: 20, color: formData.isActive ? "#16A34A" : "#9CA3AF" }} />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isActive}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            isActive: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Active"
                    sx={{ width: "100%", justifyContent: "space-between", ml: 0 }}
                    labelPlacement="start"
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    border: "1px solid #E5E7EB",
                    borderRadius: "12px",
                    px: 2,
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <ToggleOnRoundedIcon sx={{ fontSize: 20, color: formData.isIVRCaller ? "#7C3AED" : "#9CA3AF" }} />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isIVRCaller}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            isIVRCaller: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="IVR Call"
                    sx={{ width: "100%", justifyContent: "space-between", ml: 0 }}
                    labelPlacement="start"
                  />
                </Box>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={closeForm} disabled={formLoading}>
              Cancel
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={formLoading}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                background: "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg,#1D4ED8 0%,#0891B2 100%)",
                },
              }}
            >
              {formLoading ? (
                <CircularProgress size={22} sx={{ color: "#fff" }} />
              ) : formMode === "edit" ? (
                "Update Partner"
              ) : (
                "Add Partner"
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={deleteDialog} onClose={closeDeleteDialog}>
        <DialogTitle>Delete Mandi Partner</DialogTitle>

        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{" "}
            <strong>{selectedPartner?.mandiPartner}</strong> from{" "}
            <strong>{selectedPartner?.mandiLocation}</strong>?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleteLoading}>
            Cancel
          </Button>

          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleteLoading}
          >
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}